import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { triggerGlobalToast } from "@/contexts/ToastContext";
import { analytics } from "@/services/analytics";
import { useStravaConnection } from "./useStravaConnection";

/**
 * Hook to handle Strava OAuth callback result on any page
 *
 * Detects ?strava=connected|denied|error query params,
 * shows appropriate toast, cleans URL, and refreshes connection status
 */
export function useStravaConnectionResult() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetchConnection } = useStravaConnection();
  const hasHandledRef = useRef(false);

  useEffect(() => {
    const stravaStatus = searchParams.get("strava");

    // Only handle once per mount
    if (!stravaStatus || hasHandledRef.current) return;
    hasHandledRef.current = true;

    // Handle different statuses
    if (stravaStatus === "connected") {
      triggerGlobalToast("Strava connected successfully!", "success");
      analytics.track.stravaConnected(true);
      // Refresh connection status so UI updates immediately
      refetchConnection();
    } else if (stravaStatus === "denied") {
      triggerGlobalToast("Strava connection was cancelled", "info");
      analytics.track.stravaConnected(false);
    } else if (stravaStatus === "error") {
      triggerGlobalToast("Failed to connect Strava. Please try again.", "error");
      analytics.track.stravaConnected(false);
    }

    // Clean up URL by removing the strava param
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("strava");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, refetchConnection]);
}
